/**
 * API Utility Functions
 * Handles API calls with automatic JWT token inclusion
 */

// An intentionally empty production value means "use this origin" so requests
// flow through the reverse proxy. Only fall back to localhost when the variable
// is genuinely absent (the normal unconfigured development case).
const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5000';

/**
 * Make an authenticated API call
 */
const makeApiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  
  const config = {
    headers: {
      // Only set Content-Type for non-FormData requests
      ...(!(options.body instanceof FormData) && { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  // Handle token expiration
  if (response.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/auth';
    return null;
  }
  
  return response;
};

/**
 * API methods
 */
export const apiClient = {
  // GET request
  get: async (endpoint, options = {}) => {
    // Support query params via options.params
    let url = endpoint;
    if (options && options.params) {
      const qs = new URLSearchParams(options.params).toString();
      if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }
    const { params, ...rest } = options || {};
    const response = await makeApiCall(url, {
      method: 'GET',
      ...rest
    });
    return response?.json();
  },

  // POST request
  post: async (endpoint, data, options = {}) => {
    const response = await makeApiCall(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...options
    });
    return response?.json();
  },

  // PUT request
  put: async (endpoint, data, options = {}) => {
    const response = await makeApiCall(endpoint, {
      method: 'PUT',
      body: data instanceof FormData ? data : JSON.stringify(data),
      ...options
    });
    return response?.json();
  },

  // DELETE request
  delete: async (endpoint, options = {}) => {
    // Support query params via options.params
    let url = endpoint;
    if (options && options.params) {
      const qs = new URLSearchParams(options.params).toString();
      if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }
    const { params, ...rest } = options || {};
    const response = await makeApiCall(url, {
      method: 'DELETE',
      ...rest
    });
    return response?.json();
  },

  // PATCH request
  patch: async (endpoint, data, options = {}) => {
    const response = await makeApiCall(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      ...options
    });
    return response?.json();
  }
};

/**
 * Auth API methods
 */
export const authApi = {
  login: (credentials) => apiClient.post('/api/auth/login', credentials),
  register: (userData) => apiClient.post('/api/auth/register', userData),
  me: () => apiClient.get('/api/auth/me'),
  permissions: () => apiClient.get('/api/auth/permissions'),
  roles: () => apiClient.get('/api/auth/roles'),
  updateRole: (roleId, data) => apiClient.put(`/api/auth/roles/${roleId}`, data),
  forgotPassword: (email) => apiClient.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, password) => apiClient.post('/api/auth/reset-password', { token, password })
};

/**
 * Users API methods
 */
export const usersApi = {
  getAll: (params = {}) => apiClient.get('/api/users', { params }),
  getById: (id) => apiClient.get(`/api/users/${id}`),
  create: (userData) => apiClient.post('/api/users', userData),
  update: (id, userData) => apiClient.put(`/api/users/${id}`, userData),
  delete: (id) => apiClient.delete(`/api/users/${id}`),
  updateRole: (id, role) => apiClient.patch(`/api/users/${id}/role`, { role }),
  updateProfile: (id, profileData) => apiClient.patch(`/api/users/${id}/profile`, profileData)
};

/**
 * Products API methods
 */
export const productsApi = {
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiClient.get(`/api/products?${queryString}`);
  },
  getById: (id) => apiClient.get(`/api/products/${id}`),
  create: (productData, isFormData = false) => {
    if (isFormData) {
      // For FormData, use makeApiCall directly to avoid JSON.stringify
      return makeApiCall('/api/products', { 
        method: 'POST', 
        body: productData
      }).then(response => response?.json());
    }
    return apiClient.post('/api/products', productData);
  },
  update: (id, productData, isFormData = false) => {
    if (isFormData) {
      // For FormData, use makeApiCall directly to avoid JSON.stringify
      return makeApiCall(`/api/products/${id}`, { 
        method: 'PUT', 
        body: productData
      }).then(response => response?.json());
    }
    return apiClient.put(`/api/products/${id}`, productData);
  },
  delete: (id) => apiClient.delete(`/api/products/${id}`),
  approve: (id) => apiClient.patch(`/api/products/${id}/approve`),
  reject: (id) => apiClient.patch(`/api/products/${id}/reject`),
  bulkUpdateStatus: (productIds, status) => apiClient.patch('/api/products/bulk-status', { productIds, status }),
  getStats: () => apiClient.get('/api/products/stats')
};

/**
 * Auctions API methods
 */
export const auctionsApi = {
  getAll: (params = {}) => apiClient.get('/api/auction-rooms', { params }),
  getById: (id) => apiClient.get(`/api/auction-rooms/${id}`),
  create: (auctionData) => apiClient.post('/api/auction-rooms', auctionData),
  update: (id, auctionData) => apiClient.put(`/api/auction-rooms/${id}`, auctionData),
  delete: (id) => apiClient.delete(`/api/auction-rooms/${id}`),
  join: (id) => apiClient.post(`/api/auction-rooms/${id}/join`),
  leave: (id) => apiClient.post(`/api/auction-rooms/${id}/leave`)
};

/**
 * Bids API methods
 */
export const bidsApi = {
  getAll: (params = {}) => apiClient.get('/api/bids', { params }),
  getById: (id) => apiClient.get(`/api/bids/${id}`),
  create: (bidData) => apiClient.post('/api/bids', bidData),
  update: (id, bidData) => apiClient.put(`/api/bids/${id}`, bidData),
  delete: (id) => apiClient.delete(`/api/bids/${id}`)
};

/**
 * Analytics API methods
 */
export const analyticsApi = {
  getAll: (params = {}) => apiClient.get('/api/analytics', { params }),
  getDashboard: () => apiClient.get('/api/analytics/dashboard'),
  getByUser: (userId, params = {}) => apiClient.get(`/api/analytics/user/${userId}`, { params }),
  getSummary: () => apiClient.get('/api/analytics/summary'),
  create: (analyticsData) => apiClient.post('/api/analytics', analyticsData)
};

/**
 * Stats API methods
 */
export const statsApi = {
  getPlatformStats: () => apiClient.get('/api/stats/platform'),
};

/**
 * Transactions Stats API methods
 */
export const transactionsApi = {
  getOverview: () => apiClient.get('/api/transactions/stats/overview'),
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiClient.get(`/api/transactions?${qs}`);
  },
  create: (data) => apiClient.post('/api/transactions', data),
};

/**
 * Orders API methods
 */
export const ordersApi = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiClient.get(`/api/orders?${qs}`);
  },
  getById: (id) => apiClient.get(`/api/orders/${id}`),
  updateStatus: (id, statusData) => apiClient.patch(`/api/orders/${id}/status`, statusData),
  getByUser: (userId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiClient.get(`/api/orders/user/${userId}?${qs}`);
  }
};

/**
 * Second-Hand Products API methods
 */
export const secondHandApi = {
  getAll: (params = {}) => apiClient.get('/api/second-hand-products', { params }),
  getById: (id) => apiClient.get(`/api/second-hand-products/${id}`),
  create: (data) => apiClient.post('/api/second-hand-products', data),
  update: (id, data) => apiClient.put(`/api/second-hand-products/${id}`, data),
  delete: (id) => apiClient.delete(`/api/second-hand-products/${id}`),
  markSold: (id) => apiClient.put(`/api/second-hand-products/${id}/sold`),
  approveBid: (id) => apiClient.patch(`/api/second-hand-products/${id}/approve-bid`),
  getCategories: () => apiClient.get('/api/second-hand-products/meta/categories'),
  getLocations: () => apiClient.get('/api/second-hand-products/meta/locations'),
};

/**
 * Reviews API methods
 */
export const reviewsApi = {
  getAll: (params = {}) => apiClient.get('/api/reviews', { params }),
  getById: (id) => apiClient.get(`/api/reviews/${id}`),
  create: (data) => apiClient.post('/api/reviews', data),
  update: (id, data) => apiClient.put(`/api/reviews/${id}`, data),
  delete: (id) => apiClient.delete(`/api/reviews/${id}`),
};

// Product Reviews API methods
export const productReviewsApi = {
  list: (productId, params = {}) => apiClient.get(`/api/product-reviews/${productId}`),
  stats: (productId) => apiClient.get(`/api/product-reviews/${productId}/stats`),
  upsert: (productId, data) => apiClient.post(`/api/product-reviews/${productId}`, data),
  canReview: (productId) => apiClient.get(`/api/product-reviews/${productId}/can-review`),
};

/**
 * Coupons API methods
 */
export const couponsApi = {
  getAll: () => apiClient.get('/api/coupons'),
  create: (data) => apiClient.post('/api/coupons', data),
  update: (id, data) => apiClient.put(`/api/coupons/${id}`, data),
  delete: (id) => apiClient.delete(`/api/coupons/${id}`),
  redeem: (code) => apiClient.post(`/api/coupons/redeem/${code}`)
};

export const bidPackConfigsApi = {
  getAll: () => apiClient.get('/api/bid-pack-configs'),
  getAvailable: () => apiClient.get('/api/bid-pack-configs/available'),
  create: (data) => apiClient.post('/api/bid-pack-configs', data),
  update: (id, data) => apiClient.put(`/api/bid-pack-configs/${id}`, data),
  delete: (id) => apiClient.delete(`/api/bid-pack-configs/${id}`)
};

export const brandsApi = {
  getAll: (params) => apiClient.get('/api/brands', { params }),
  getById: (id) => apiClient.get(`/api/brands/${id}`),
  create: (data) => apiClient.post('/api/brands', data),
  update: (id, data) => apiClient.put(`/api/brands/${id}`, data),
  delete: (id) => apiClient.delete(`/api/brands/${id}`),
  toggleActive: (id) => apiClient.patch(`/api/brands/${id}/toggle-active`)
};

export default apiClient;
